import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiMailLine, RiUserAddLine, RiLoader4Line, RiErrorWarningLine, RiCheckLine, RiDeleteBinLine } from '@remixicon/react';
import { toast } from 'sonner';
import { TEAM_ROLES } from '@/constants';
import { formatMemberStatus } from '../utils';

/**
 * Drives the Status section's action button in edit mode.
 *   ONBOARDING_COMPLETED → ACTIVE   (Activate)
 *   ACTIVE               → INACTIVE (Deactivate, destructive)
 *   INACTIVE             → ACTIVE   (Reactivate)
 * Other statuses (ONBOARDING_STARTED / SUSPENDED / ARCHIVED) render as a
 * read-only badge with a "managed by support" note.
 */
const ACTIONS_BY_STATUS = {
  ONBOARDING_COMPLETED: { value: 'ACTIVE',   label: 'Activate Member',   kind: 'primary' },
  ACTIVE:               { value: 'INACTIVE', label: 'Deactivate',        kind: 'destructive' },
  INACTIVE:             { value: 'ACTIVE',   label: 'Reactivate Member', kind: 'primary' },
};

const Field = ({ label, required, children, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const AddMemberDrawer = ({
  open,
  onClose,
  onSubmit,
  onViewPermissions,
  onChangeStatus,
  initialValues = null,
  currentUserprofileId = null,
  ownedBy = null,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  // Status flow — status mutation runs independently of the form submit so the
  // audit-relevant write isn't bundled with name/role edits.
  const [statusSaving, setStatusSaving] = useState(false);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  // Confirm dialog for the destructive "Remove" action (archives the member).
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const isEdit = !!initialValues;
  // Org-owner gates: only the owner can edit the owner. Admins viewing the
  // owner's drawer (defense-in-depth — the panel already hides Edit) get a
  // read-only role section + no status actions.
  const targetIsOwner = !!ownedBy && initialValues?.userprofile_id === ownedBy;
  const viewerIsOwner = !!ownedBy && currentUserprofileId === ownedBy;
  const ownerLocked = targetIsOwner && !viewerIsOwner;
  const currentStatus = (initialValues?.status || '').toUpperCase();
  // Treat a missing/null status as "needs activation" — show the Activate button
  // and a Pending-Activation badge, same as an ONBOARDING_COMPLETED member.
  const statusBadge = formatMemberStatus(currentStatus || 'ONBOARDING_COMPLETED');
  const nextStatus = currentStatus
    ? ACTIONS_BY_STATUS[currentStatus] || null
    : ACTIONS_BY_STATUS.ONBOARDING_COMPLETED;
  const isCurrentUser =
    !!currentUserprofileId && initialValues?.userprofile_id === currentUserprofileId;

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || initialValues?.email || 'this member';

  const requestStatusChange = (target) => {
    if (target === 'INACTIVE') {
      setConfirmDeactivateOpen(true);
      return;
    }
    void applyStatusChange(target);
  };

  const applyStatusChange = async (target) => {
    if (!onChangeStatus || statusSaving) return;
    setStatusSaving(true);
    try {
      await onChangeStatus(target);
      setConfirmDeactivateOpen(false);
      setConfirmRemoveOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to update status', { position: 'bottom-right' });
    } finally {
      setStatusSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setFirstName(initialValues?.firstName || '');
      setLastName(initialValues?.lastName || '');
      setEmail(initialValues?.email || '');
      setRole(initialValues?.role || '');
      setSaving(false);
    }
  }, [
    open,
    initialValues?.firstName,
    initialValues?.lastName,
    initialValues?.email,
    initialValues?.role,
  ]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit =
    firstName.trim() && lastName.trim() && emailValid && role && !saving && !ownerLocked;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = { firstName, lastName, email, role };
      const result = await onSubmit?.(payload, initialValues);
      // The parent may defer the actual write to a follow-up modal
      // (e.g. Restore an archived member, Activate a pending-activation
      // member, or block cross-org). In those cases the parent returns
      // `{ deferred: true }` so we don't fire a misleading success toast
      // here — the modal will run its own confirm/toast.
      //
      // Info-only decisions (already_member / crossorg / onboarding_started
      // / suspended) additionally set `keepOpen: true` so the drawer stays
      // open under the notice — the admin can edit the email and retry
      // without re-opening the drawer.
      if (!result?.keepOpen) onClose();
      if (!result?.deferred) {
        toast.success(isEdit ? 'Member updated' : 'Invitation sent', { position: 'bottom-right' });
      }
    } catch (err) {
      toast.error(
        err?.message || (isEdit ? 'Failed to update member' : 'Failed to add member'),
        { position: 'bottom-right' }
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[9998] bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 z-[9999] w-full sm:w-[560px] lg:w-[640px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          <h3 className="text-lg font-semibold text-primary-700">
            {targetIsOwner ? 'Edit Owner' : (isEdit ? 'Edit member' : 'Add new member')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-4">
          {/* Status — edit-mode only. The Activate / Deactivate / Reactivate
              action is its own write (independent of the form submit). */}
          {isEdit && (
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusBadge.className}`}>
                  {statusBadge.label}
                </span>
              </div>

              {nextStatus && !isCurrentUser && !ownerLocked && (
                <button
                  type="button"
                  onClick={() => requestStatusChange(nextStatus.value)}
                  disabled={statusSaving}
                  className={`w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                    nextStatus.kind === 'destructive'
                      ? 'border border-red-300 text-red-600 hover:bg-red-50'
                      : 'border border-primary-50 bg-primary-50 text-primary-500 hover:bg-primary-100'
                  }`}
                >
                  {statusSaving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                  {statusSaving ? 'Saving…' : nextStatus.label}
                </button>
              )}

              {!nextStatus && !ownerLocked && (
                <p className="text-xs text-gray-500">
                  This status is managed by support. Contact admin support to change it.
                </p>
              )}

              {ownerLocked && (
                <p className="text-xs text-gray-500">Only the org owner can change this member's status.</p>
              )}

              {isCurrentUser && !ownerLocked && (
                <p className="text-xs text-gray-500">You can't change your own status here.</p>
              )}
            </section>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" required>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </Field>
            <Field label="Last Name" required>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </Field>
          </div>

          <Field label="Email Address" required>
            <div className="relative flex items-center">
              <RiMailLine className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                readOnly={isEdit}
                className={`w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  isEdit ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
                }`}
              />
            </div>
          </Field>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Role<span className="text-red-500 ml-0.5">*</span>
              </label>
              {onViewPermissions && (
                <button
                  type="button"
                  onClick={onViewPermissions}
                  className="text-xs text-gray-700"
                >
                  View Permissions
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TEAM_ROLES.map((r) => {
                const selected = role === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => !ownerLocked && setRole(r.value)}
                    disabled={ownerLocked}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${
                      selected
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    } ${ownerLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <span>{r.label}</span>
                    <span
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        selected ? 'border-primary-600' : 'border-gray-300'
                      }`}
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-primary-600" />}
                    </span>
                  </button>
                );
              })}
            </div>
            {ownerLocked && (
              <p className="text-xs text-gray-500 mt-2">Only the org owner can change this member's role.</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          {isEdit ? (
            <div className="flex items-center gap-2">
              {/* Remove member — archives the user. Hidden for self-edit and
                  for the org owner (defense in depth alongside the panel gate). */}
              {!isCurrentUser && !ownerLocked && (
                <button
                  type="button"
                  onClick={() => setConfirmRemoveOpen(true)}
                  disabled={statusSaving || saving}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium border border-red-300 text-red-600 bg-white hover:bg-red-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-label="Remove member"
                >
                  <RiDeleteBinLine className="w-4 h-4" />
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSubmit}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
                  canSubmit
                    ? 'bg-primary-700 text-white hover:bg-primary-800'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RiCheckLine className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSubmit}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
                canSubmit
                  ? 'bg-primary-700 text-white hover:bg-primary-800'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <RiUserAddLine className="w-4 h-4" />
              {saving ? 'Adding...' : 'Add Member'}
            </button>
          )}
        </div>
      </div>

      {/* Destructive confirm — Deactivate Member. Custom portal at z-[10000] so
          it stacks ABOVE the drawer (z-[9999]) instead of behind it like the
          shared Modal primitive (which is only z-[200]). */}
      {confirmDeactivateOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !statusSaving && setConfirmDeactivateOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-primary-500">Deactivate member?</h2>
              <button
                type="button"
                onClick={() => !statusSaving && setConfirmDeactivateOpen(false)}
                disabled={statusSaving}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  {statusSaving
                    ? <RiLoader4Line className="w-5 h-5 text-red-600 animate-spin" />
                    : <RiErrorWarningLine className="w-5 h-5 text-red-600" />}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Deactivate <span className="font-semibold">{fullName}</span>?
                  They'll no longer be able to sign in until reactivated.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmDeactivateOpen(false)}
                  disabled={statusSaving}
                  className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => applyStatusChange('INACTIVE')}
                  disabled={statusSaving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-red-600 rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statusSaving && <RiLoader4Line className="w-4 h-4 animate-spin" />}
                  {statusSaving ? 'Deactivating…' : 'Deactivate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Destructive confirm — Remove Member (archives them). Same z-[10000]
          stacking so it sits above the drawer. */}
      {confirmRemoveOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !statusSaving && setConfirmRemoveOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="px-6 py-5">
              <h2 className="text-lg font-bold text-gray-900">Remove member</h2>
              <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                Removing <span className="font-semibold">{fullName}</span> from your
                organization transfers all their client data to the administrator.
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-4">
                Do you want to continue?
              </p>
              <div className="flex items-center gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => setConfirmRemoveOpen(false)}
                  disabled={statusSaving}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white rounded-full border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No, cancel
                </button>
                <button
                  type="button"
                  onClick={() => applyStatusChange('ARCHIVED')}
                  disabled={statusSaving}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statusSaving
                    ? <RiLoader4Line className="w-4 h-4 animate-spin" />
                    : <RiDeleteBinLine className="w-4 h-4" />}
                  {statusSaving ? 'Removing…' : 'Yes, remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default AddMemberDrawer;
